import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { log } from '@/lib/log';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useFinanceMeta = () => {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/finance/meta`)
      .then((r) => { if (!cancelled) setMeta(r.data); })
      .catch((err) => { log.error('[useFinanceMeta] failed:', err); });
    return () => { cancelled = true; };
  }, []);
  return meta;
};

export const useFinanceOverview = (period, start, end) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') {
        if (start) params.start = start;
        if (end) params.end = end;
      }
      const r = await axios.get(`${API}/finance/overview`, { params });
      setData(r.data);
    } catch {
      toast.error('Failed to load finance overview');
    } finally {
      setLoading(false);
    }
  }, [period, start, end]);
  useEffect(() => { refresh(); }, [refresh]);
  return { data, loading, refresh };
};

export const useCompanyFinance = (clientId, period, start, end) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') {
        if (start) params.start = start;
        if (end) params.end = end;
      }
      const r = await axios.get(`${API}/finance/company/${clientId}`, { params });
      setData(r.data);
    } catch {
      toast.error('Failed to load company finance');
    } finally {
      setLoading(false);
    }
  }, [clientId, period, start, end]);
  useEffect(() => { refresh(); }, [refresh]);
  return { data, loading, refresh };
};

export const createStatement = async (payload) => {
  const r = await axios.post(`${API}/finance/statements`, payload);
  return r.data.statement;
};

export const updateStatement = async (id, payload) => {
  const r = await axios.patch(`${API}/finance/statements/${id}`, payload);
  return r.data.statement;
};

export const deleteStatement = async (id) => {
  await axios.delete(`${API}/finance/statements/${id}`);
};

export const exportFinance = async (fmt, scope, params = {}) => {
  const url = `${API}/finance/export/${fmt}`;
  const r = await axios.get(url, { params: { scope, ...params }, responseType: 'blob' });
  const blob = new Blob([r.data]);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  const disp = r.headers['content-disposition'] || '';
  const m = disp.match(/filename=([^;]+)/);
  link.download = m ? m[1].trim() : `finance.${fmt}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(link.href);
};
